import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, ChevronLeft, ExternalLink, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AiKeyCard = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/ai-key`).then(r => setStatus(r.data)).catch(() => {});
  }, [open]);

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/ai-key/test`);
      setTestResult(r.data);
      if (r.data.ok) toast.success('המפתח תקין — ה-AI עובד ✓');
      else toast.error(r.data.error || 'המפתח לא תקין');
    } catch (_e) {
      setTestResult({ ok: false, error: 'שגיאה בבדיקה' });
    } finally { setTesting(false); }
  };

  const saveKey = async () => {
    if (!keyInput.trim()) { toast.error('הדבק את המפתח קודם'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ai-key`, { key: keyInput.trim() });
      toast.success('המפתח נבדק, אושר ונשמר ✓', { duration: 4000 });
      setKeyInput('');
      setTestResult({ ok: true });
      const r = await axios.get(`${API}/admin/ai-key`);
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'שמירה נכשלה', { duration: 6000 });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-gradient-to-br from-[#1c2333] to-[#2a3450] rounded-xl shadow-sm mb-4 text-white overflow-hidden" data-testid="ai-key-card">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
        data-testid="toggle-ai-key"
      >
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-sky-400" />
          <h3 className="font-bold text-sm" style={{ fontFamily: 'Heebo, sans-serif' }}>מפתח AI (GPT) — סטטוס ואישור</h3>
          {testResult && (testResult.ok
            ? <span className="text-[11px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">תקין ✓</span>
            : <span className="text-[11px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full">שגיאה</span>)}
        </div>
        <ChevronLeft className={`w-5 h-5 transition-transform ${open ? '-rotate-90' : 'rotate-0'}`} />
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-3">
          <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 flex-wrap gap-2">
            <div className="text-xs text-stone-300">
              מפתח נוכחי: <code className="text-sky-300" dir="ltr" data-testid="ai-key-masked">{status?.masked || 'טוען...'}</code>
              {status?.source === 'dashboard' && <span className="mr-2 text-[10px] text-emerald-300">(הוגדר מהדשבורד)</span>}
            </div>
            <button
              type="button"
              onClick={runTest}
              disabled={testing}
              className="bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="ai-key-test-btn"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              בדוק שהמפתח תקין
            </button>
          </div>
          {testResult && !testResult.ok && (
            <div className="flex items-start gap-2 bg-red-500/15 border border-red-400/40 rounded-lg px-3 py-2 text-xs text-red-200" data-testid="ai-key-error">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{testResult.error}</span>
            </div>
          )}
          <div className="space-y-1.5">
            <p className="text-xs text-stone-300 font-bold">החלפת מפתח (הדבק ואשר):</p>
            <div className="flex gap-2">
              <input
                type="text"
                dir="ltr"
                value={keyInput}
                onChange={e => setKeyInput(e.target.value)}
                placeholder="sk-emergent-..."
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs text-white placeholder:text-stone-400 focus:outline-none focus:border-sky-400"
                data-testid="ai-key-input"
              />
              <button
                type="button"
                onClick={saveKey}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-50 whitespace-nowrap"
                data-testid="ai-key-save-btn"
              >
                {saving ? 'בודק...' : 'אשר ושמור'}
              </button>
            </div>
            <p className="text-[11px] text-stone-400">המפתח נבדק מול ה-AI לפני שמירה — נשמר רק אם הוא עובד. תקף מיד לסריקת חשבוניות, בוט וואטסאפ וקמפיינים.</p>
          </div>
          <a
            href="https://app.emergent.sh/profile"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-sky-300 hover:text-sky-200 underline"
            data-testid="ai-key-profile-link"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            פתח את עמוד המפתח והטעינה (Profile ← Universal Key)
          </a>
        </div>
      )}
    </div>
  );
};
